import clsx from 'clsx';
import { useState } from 'react';
import { BiMessage } from 'react-icons/bi';
import { useNavigate } from 'react-router-dom';

import { ButtonOutlined } from '@/components/Button';
import { useAuth } from '@/store/auth/auth.store';
import { getVesselCrewChats } from '@/store/chats/chat.api';
import { isInOwnerGroup } from '@/utils/roles';
import { toast } from '@/utils/toast';

import s from './EventSidePanel.module.scss';

interface EventChatButtonsProps {
  vesselId?: string;
  isGuest: boolean;
  /** The event's chat room. Absent on an event that never got one — an idea trip. */
  eventChatId?: string | null;
  closePanel: () => void;
}

export function EventChatButtons({
  vesselId,
  isGuest,
  eventChatId,
  closePanel,
}: EventChatButtonsProps) {
  const navigate = useNavigate();
  const userRoles = useAuth((state) => state.session?.userRoles);
  const [isOpeningCrewChat, setIsOpeningCrewChat] = useState(false);

  const isOwner = isInOwnerGroup(userRoles);

  const openCrewChat = async () => {
    if (isOpeningCrewChat) return;
    setIsOpeningCrewChat(true);
    try {
      const { data } = await getVesselCrewChats();
      navigate(`/inbox/${data?.crew_chat_id}`);
    } catch {
      toast.error('Couldn’t open the crew chat. Please try again.');
    } finally {
      setIsOpeningCrewChat(false);
    }
  };

  // Only the chats that exist. An event chat is created with the event, so an
  // event without one — an idea trip — has nothing to open, and a button that
  // only ever answers "no chat yet" is worse than no button.
  const buttons = [
    vesselId && !isGuest
      ? {
          key: 'vessel',
          label: 'Vessel',
          onClick: () => {
            navigate(`/inbox/${vesselId}`);
            closePanel();
          },
        }
      : null,
    !isOwner && !isGuest && vesselId
      ? {
          key: 'crew',
          label: 'Crew',
          disabled: isOpeningCrewChat,
          onClick: () => {
            void openCrewChat();
            closePanel();
          },
        }
      : null,
    eventChatId
      ? {
          key: 'event',
          label: 'Event',
          onClick: () => {
            navigate(`/inbox/${eventChatId}`);
            closePanel();
          },
        }
      : null,
  ].filter((b): b is NonNullable<typeof b> => !!b);

  if (buttons.length === 0) return null;

  // One segmented control: rounded on the outside, square where they meet. A
  // lone button keeps its own radius and takes the whole row instead — a single
  // short pill floating in the middle of the panel reads as an afterthought.
  const grouped = buttons.length > 1;

  return (
    <>
      <p>Chats</p>
      <div className={s.chatRow}>
        {buttons.map((button, i) => (
          <ButtonOutlined
            key={button.key}
            className={clsx(
              grouped ? s.groupChatButton : s.soloChatButton,
              grouped && i === 0 && s.leftButton,
              grouped && i === buttons.length - 1 && s.rightButton,
              grouped && i > 0 && i < buttons.length - 1 && s.centerButton,
            )}
            disabled={button.disabled}
            onClick={button.onClick}
          >
            <BiMessage size={20} /> {button.label}
          </ButtonOutlined>
        ))}
      </div>
    </>
  );
}
